import React from "react";
import {
  Paper,
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
} from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BiotechIcon from "@mui/icons-material/Biotech";
import BarChartIcon from "@mui/icons-material/BarChart";
import LabelIcon from "@mui/icons-material/Label";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CommentIcon from "@mui/icons-material/Comment";
import HistoryIcon from "@mui/icons-material/History";

const SectionHeader = ({ icon, title }) => (
  <Grid item xs={12}>
    <Box display="flex" alignItems="center" gap={0.75} mt={1.5} mb={0.25}>
      <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
      <Typography variant="caption" fontWeight={700} color="text.secondary" letterSpacing={0.8} textTransform="uppercase">
        {title}
      </Typography>
      <Box flex={1} height="1px" bgcolor="divider" ml={0.5} />
    </Box>
  </Grid>
);

const FieldCard = ({ label, value, xs = 3 }) => (
  <Grid item xs={xs}>
    <Box
      sx={{
        bgcolor: "grey.50",
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 1.5,
        px: 1.25,
        py: 0.75,
        height: "100%",
        transition: "all 0.15s ease",
        "&:hover": { borderColor: "primary.light", bgcolor: "primary.50" },
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.4} display="block" fontSize="0.6rem">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} fontSize="0.8rem" color={value != null && value !== "" ? "text.primary" : "text.disabled"}>
        {value != null && value !== "" ? value : "N/A"}
      </Typography>
    </Box>
  </Grid>
);

const ViewOutsourcedAnalysis = ({ analysis }) => {
  if (!analysis) return null;

  const formatDate = (val) =>
    val ? new Date(val).toLocaleString() : null;

  return (
    <Paper
      elevation={0}
      sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 2, p: 2, mb: 2 }}
    >
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle1" fontWeight={700} letterSpacing={-0.3}>
            Outsourced Analysis
          </Typography>
          <Typography variant="caption" color="text.secondary">— Laboratory Record</Typography>
        </Box>
        <Chip label="ELI / Eurofins" color="primary" variant="outlined" size="small" icon={<BiotechIcon sx={{ fontSize: 14 }} />} />
      </Box>

      <Divider sx={{ mb: 0.5 }} />

      <Grid container spacing={1}>
        {/* Order + Lab — 4 fields in one row */}
        <SectionHeader icon={<AssignmentIcon sx={{ fontSize: 14 }} />} title="Order & Lab Information" />
        <FieldCard label="Order Number" value={analysis.orderNumber} />
        <FieldCard label="Sample Number" value={analysis.sampleNumber} />
        <FieldCard label="Analysis By Lab" value={analysis.analysisByLab} />
        <FieldCard label="Test Code" value={analysis.testCode} />

        {/* Analytical Parameters + Results — 4 fields in one row */}
        <SectionHeader icon={<ScienceIcon sx={{ fontSize: 14 }} />} title="Analytical Parameters & Results" />
        <FieldCard label="Analyte Name" value={analysis.analyteName} />
        <FieldCard label="Result / Concentration" value={analysis.resultConcentration} />
        <FieldCard label="Unit" value={analysis.unit} />
        <FieldCard label="Reporting Limit" value={analysis.reportingLimit} />

        {/* Dilution + IDs + Date — 4 fields in one row */}
        <SectionHeader icon={<LabelIcon sx={{ fontSize: 14 }} />} title="Identifiers & Timing" />
        <FieldCard label="Dilution Factor" value={analysis.dilutionFactor ?? null} />
        <FieldCard label="ELI Sample Lab ID" value={analysis.eliSampleLabId} />
        <FieldCard label="Eurofins Sample Lab ID" value={analysis.eurofinsSampleLabId} />
        <FieldCard label="Date / Time of Analysis" value={formatDate(analysis.analysisDateTime)} />

        {/* Additional Comments */}
        {analysis.additionalComments && (
          <>
            <SectionHeader icon={<CommentIcon sx={{ fontSize: 14 }} />} title="Additional Comments" />
            <Grid item xs={12}>
              <Box sx={{ bgcolor: "grey.50", border: "1px solid", borderColor: "grey.200", borderRadius: 1.5, px: 1.25, py: 0.75 }}>
                <Typography variant="body2" fontSize="0.8rem" color="text.secondary" lineHeight={1.6}>
                  {analysis.additionalComments}
                </Typography>
              </Box>
            </Grid>
          </>
        )}

        {/* Audit Trail — up to 4 per row */}
        <SectionHeader icon={<HistoryIcon sx={{ fontSize: 14 }} />} title="Audit Trail" />
        <FieldCard label="Created At" value={formatDate(analysis.createdAt)} />
        <FieldCard label="Created By" value={analysis.createdBy} />
        {analysis.updatedAt && <FieldCard label="Updated At" value={formatDate(analysis.updatedAt)} />}
        {analysis.updatedBy && <FieldCard label="Updated By" value={analysis.updatedBy} />}
        {analysis.approvedAt && <FieldCard label="Approved At" value={formatDate(analysis.approvedAt)} />}
        {analysis.approvedBy && <FieldCard label="Approved By" value={analysis.approvedBy} />}
      </Grid>
    </Paper>
  );
};

export default ViewOutsourcedAnalysis;